import React from 'react';
import { Popover, Divider } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  UilAngleDown,
  UilSignout,
  UilUser,
  UilCheckCircle,
  UilExclamationTriangle,
} from '@iconscout/react-unicons';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useAuth } from '../../../context/AuthProvider';

const ROLE_ROUTES = {
  Company: '/company',
  Filer: '/filer',
  ERMD: '/ermd',
  SOD: '/sod',
};

const STATUS_STYLES = {
  Active:   { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', dot: '#22c55e' },
  Inactive: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', dot: '#ef4444' },
  Verified: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  Pending:  { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  default:  { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' },
};

function StatusBadge({ status, dot = false }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.default;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 600, padding: '2px 8px',
      borderRadius: 999, border: `1px solid ${s.border}`,
      background: s.bg, color: s.color, whiteSpace: 'nowrap',
    }}>
      {dot && (
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: s.dot || s.color, flexShrink: 0,
        }} />
      )}
      {status}
    </span>
  );
}

function AccountAvatar({ name, size = 32, bg = '#0f8269', color = '#fff', fontSize = 13 }) {
  const initials = (name?.[0] ?? 'U').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: bg, color, fontSize, fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {initials}
    </div>
  );
}

const AuthInfo = () => {
  const { logout, switchRole, multipleRoles } = useAuth();
  const { user, removeUser, signoutRedirect, clearStaleState } = useOidcAuth();
  const navigate = useNavigate();

  const storedData = JSON.parse(localStorage.getItem('user_data') || '{}');

  const SignOut = async () => {
    logout();
    try {
      removeUser();
      await signoutRedirect({
        id_token_hint: user?.id_token,
        post_logout_redirect_uri: 'http://localhost:3000/',
      });
      clearStaleState();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleSwitchAccount = (roleObj) => {
    switchRole(roleObj);
    navigate(ROLE_ROUTES[roleObj.role_name] || '/');
  };

  const otherAccounts = Array.isArray(multipleRoles)
    ? multipleRoles.filter((r) => r.user_id !== storedData?.user_id)
    : [];

  const role = storedData?.role_name?.toLowerCase() || '';
  const fullName = storedData?.first_name
    ? `${storedData.first_name} ${storedData?.last_name || ''}`.trim()
    : storedData?.username || storedData?.company_name || 'User';
  const initials = (
    storedData?.first_name?.[0] ||
    storedData?.username?.[0] ||
    storedData?.company_name?.[0] ||
    'U'
  ).toUpperCase();

  const isCompanyRole = storedData?.role_name === 'Company' || storedData?.role_name === 'Researcher';

  const content = (
    <div style={{ width: 300 }}>

      {/* ── Header ── */}
      <div style={{
        background: '#f9fafb',
        borderBottom: '1px solid #f3f4f6',
        borderRadius: '12px 12px 0 0',
        padding: '16px 18px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
          background: '#0f8269',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 800, color: '#fff',
          boxShadow: '0 2px 8px rgba(15,130,105,0.35)',
        }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fullName}
            </p>
            {storedData?.user_status && <StatusBadge status={storedData.user_status} dot />}
          </div>
          <p style={{ margin: 0, fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
            @{storedData?.username || '—'}
          </p>
          {storedData?.email && (
            <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {storedData.email}
            </p>
          )}
        </div>
      </div>

      {/* ── Current account context (Company/Researcher) ── */}
      {isCompanyRole && storedData?.company_name && (
        <div style={{ padding: '12px 18px 0' }}>
          <div style={{
            background: '#f9fafb', border: '1px solid #f3f4f6',
            borderRadius: 10, padding: '10px 12px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#1d4ed8',
            }}>
              {storedData.company_name[0]}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {storedData.company_name}
              </p>
              {storedData?.company_status && (
                <div style={{ marginTop: 3 }}>
                  <StatusBadge status={storedData.company_status} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Switch Account ── */}
      {otherAccounts.length > 0 && (
        <div style={{ padding: '14px 20px 0' }}>
          <p style={{
            margin: '0 0 8px', fontSize: 10, fontWeight: 700,
            color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em',
          }}>
            Switch Account
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {otherAccounts.map((acc) => (
              <button
                key={acc.user_id}
                type="button"
                onClick={() => handleSwitchAccount(acc)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', borderRadius: 10, cursor: 'pointer',
                  border: '1px solid #f3f4f6', background: '#fafafa',
                  textAlign: 'left', width: '100%',
                  transition: 'background 0.12s, border-color 0.12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#bbf7d0'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = '#f3f4f6'; }}
              >
                <AccountAvatar
                  name={acc.company_name || acc.role_name}
                  size={32}
                  bg="linear-gradient(135deg, #dbeafe, #bfdbfe)"
                  color="#1d4ed8"
                  fontSize={13}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {acc.company_name || acc.role_name}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>@{acc.username}</span>
                    {acc.company_status && <StatusBadge status={acc.company_status} />}
                  </div>
                </div>
                <div style={{ color: '#0f8269', opacity: 0.7, flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div style={{ padding: '8px 18px 14px' }}>
        <Divider style={{ margin: '0 0 10px' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Link
            to={`/${role}/profile`}
            style={{ textDecoration: 'none' }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
              color: '#374151', transition: 'background 0.12s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <UilUser size="16" style={{ color: '#6b7280', flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 500 }}>My Profile</span>
            </div>
          </Link>

          <button
            type="button"
            onClick={SignOut}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
              color: '#dc2626', background: 'transparent',
              border: 'none', width: '100%', textAlign: 'left',
              transition: 'background 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <UilSignout size="16" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Sign Out</span>
          </button>
        </div>
      </div>

    </div>
  );

  return (
    <Popover
      placement="bottomRight"
      content={content}
      trigger="click"
      overlayStyle={{ paddingTop: 6 }}
      overlayInnerStyle={{ padding: 0, borderRadius: 14, overflow: 'hidden' }}
    >
      <button
        type="button"
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#f9fafb', border: '1px solid #e5e7eb',
          borderRadius: 10, padding: '6px 10px 6px 8px',
          cursor: 'pointer', transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#f9fafb'; }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: '50%',
          background: '#0f8269',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ textAlign: 'left' }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {fullName}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', lineHeight: 1.2 }}>
            {storedData?.role_name || ''}
          </p>
        </div>
        <UilAngleDown size="15" style={{ color: '#9ca3af', marginLeft: 2, flexShrink: 0 }} />
      </button>
    </Popover>
  );
};

export default AuthInfo;
